/**
 * 공통 컴포넌트 Props 인터페이스
 *
 * 모든 T prefix 컴포넌트에서 재사용 가능한 기본 Props 타입을 정의합니다.
 * 네이티브 HTML 속성(aria-*, data-*, style, role 등)을 기본 지원합니다.
 */

/**
 * 모든 컴포넌트의 가장 기본이 되는 Props
 *
 * React.HTMLAttributes를 확장하여 네이티브 HTML 속성을 기본 지원합니다.
 * aria-label, data-testid, style, role, tabIndex 등을 자유롭게 사용 가능합니다.
 *
 * @property id - HTML 요소의 고유 식별자
 * @property className - 추가 CSS 클래스명
 * @property dataAttributes - data-* 속성으로 변환될 커스텀 속성들 (deprecated: data-* 속성을 직접 사용하세요)
 */
export interface BaseComponentProps extends Omit<React.HTMLAttributes<HTMLElement>, "color" | "onChange" | "onClick" | "prefix"> {
	/** @deprecated data-* 속성을 직접 사용하세요. 예: data-testid="my-button" */
	dataAttributes?: Record<string, string | number | boolean>
}

/**
 * children을 받는 컴포넌트용 Props
 */
export interface BaseComponentWithChildrenProps extends BaseComponentProps {
	children?: React.ReactNode
}

/**
 * 인터랙티브한 요소(버튼, 입력 등)의 기본 Props
 *
 * @property disabled - 비활성화 상태
 */
export interface BaseInteractiveProps extends BaseComponentProps {
	disabled?: boolean
}

/**
 * 크기 조절 가능한 컴포넌트용 Props
 *
 * @property size - 컴포넌트 크기 (컴포넌트별로 세부 타입 재정의 가능)
 */
export interface BaseSizableProps extends BaseComponentProps {
	size?: string
}

/**
 * 폼 요소의 기본 Props
 *
 * @property required - 필수 입력 여부
 * @property error - 에러 상태
 * @property readOnly - 읽기 전용 여부
 */
export interface BaseFormElementProps extends BaseInteractiveProps {
	required?: boolean
	error?: boolean
	readOnly?: boolean
}

/**
 * 입력 요소의 기본 Props
 */
export interface BaseInputProps extends BaseFormElementProps {
	name?: string
	placeholder?: string
	autoFocus?: boolean
}

/**
 * restProps에서 aria-*, role 속성만 분리한다.
 *
 * BaseComponentProps가 HTMLAttributes를 확장해 aria-label 등이 restProps에 섞여 들어오는데,
 * 이를 그대로 래퍼 요소(div 등)에 스프레드하면 실제 폼 요소(input 등)는 이름 없는 채로 남는다.
 * 분리된 aria 속성은 호출부에서 내부 폼 요소에 직접 전달해야 한다.
 */
export function splitAriaProps<T extends object>(props: T): { ariaProps: React.AriaAttributes & { role?: React.AriaRole }; rest: Omit<T, keyof React.AriaAttributes | "role"> } {
	const ariaProps: Record<string, unknown> = {}
	const rest: Record<string, unknown> = {}
	Object.entries(props).forEach(([key, val]) => {
		if (key === "role" || key.startsWith("aria-")) {
			ariaProps[key] = val
		} else {
			rest[key] = val
		}
	})
	return { ariaProps: ariaProps as React.AriaAttributes & { role?: React.AriaRole }, rest: rest as Omit<T, keyof React.AriaAttributes | "role"> }
}
